import UiLanguageSelect from "../components/UiLanguageSelect";
import { useTranslation } from "react-i18next";
// src/pages/Login.tsx
import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "../api/client";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { resendVerification } from "../api/auth";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";
import { hasUpgradeIntent, postLoginPath, upgradeIntentSearch } from "../utils/upgradeIntent";
import type { AuthMe } from "../types/auth";

type LoginOutcome =
  | { kind: "local"; code: "loginFailed" | "noCompany" }
  | { kind: "known"; key: "login.invalidCredentials" | "login.verifyBeforeLogin" | "accountInlays.errors.tooManyRequests" }
  | { kind: "raw"; value: string };

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [error, setError] = useState<LoginOutcome | null>(null);
  const [logoOk, setLogoOk] = useState(true);

  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");

  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const [sessionExpired, setSessionExpired] = useState(
    params.get("expired") === "1"
  );
  const [registered, setRegistered] = useState(
    params.get("registered") === "1"
  );
  const [passwordChanged, setPasswordChanged] = useState(
    params.get("password_changed") === "1"
  );

  const labelCls = `block mb-1 ${A.label}`;

  useEffect(() => {
    const signupModes = new URLSearchParams(location.search).getAll("mode");
    if (signupModes.length === 1 && signupModes[0] === "signup") {
      navigate(`/register${upgradeIntentSearch(location.search)}`, { replace: true });
      return;
    }
    const token = localStorage.getItem("access_token");
    if (!token || !hasUpgradeIntent(location.search)) return;
    let active = true;
    apiGet<AuthMe>("/auth/me").then(user => {
      if (!active || localStorage.getItem("access_token") !== token || !user.is_active) return;
      const target = postLoginPath(user.company_id, location.search);
      if (target) navigate(target, { replace: true });
    }).catch(() => { /* An expired session keeps the ordinary login form. */ });
    return () => { active = false; };
  }, [location.search, navigate]);
  const alertBase = `mt-4 rounded-2xl border px-4 py-3 ${A.body} text-center`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSessionExpired(false);
    setRegistered(false);
    setPasswordChanged(false);
    setNeedsVerification(false);
    setResendStatus("idle");

    try {
      const result = await apiPost("/auth/login", {
        email: email.trim().toLowerCase(),
        password,
      });

      localStorage.setItem("access_token", result.access_token);
      sessionStorage.setItem("access_token", result.access_token);

      const target = result.company_id && postLoginPath(result.company_id, location.search);
      if (target) {
        navigate(target);
      } else {
        setError({ kind: "local", code: "noCompany" });
      }
    } catch (err: any) {
      let outcome: LoginOutcome = { kind: "local", code: "loginFailed" };
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;

      if (status === 401 && detail === "Invalid email or password") outcome = { kind: "known", key: "login.invalidCredentials" };
      else if (status === 403 && detail === "Please verify your email before logging in.") outcome = { kind: "known", key: "login.verifyBeforeLogin" };
      else if (status === 429 && detail === "Too many requests. Please try again later.") outcome = { kind: "known", key: "accountInlays.errors.tooManyRequests" };
      else if (typeof detail === "string" && detail.trim()) outcome = { kind: "raw", value: detail };
      else if (typeof err === "string") outcome = { kind: "raw", value: err };
      else if (err?.message) outcome = { kind: "raw", value: err.message };

      if (
        status === 403 &&
        typeof detail === "string" &&
        detail.toLowerCase().includes("verify your email")
      ) {
        setNeedsVerification(true);
      } else {
        setNeedsVerification(false);
      }

      setError(outcome);
      setSessionExpired(false);
      setPasswordChanged(false);
    }
  };

  const handleResendVerification = async () => {
    const emailNorm = email.trim().toLowerCase();
    if (!emailNorm) return;

    try {
      setResendStatus("sending");
      await resendVerification(emailNorm);
      setResendStatus("sent");
    } catch (err) {
      console.error(err);
      setResendStatus("error");
    }
  };

  const resendLabel =
    resendStatus === "sending"
      ? t("login.sendingDots")
      : resendStatus === "sent"
      ? t("login.verificationSent")
      : resendStatus === "error"
      ? t("login.resendFailed")
      : t("login.resendVerification");

  const hasError = error?.kind === "raw" ? !!error.value : !!error;

  const resendDisabled = resendStatus === "sending" || resendStatus === "sent";

  return (
    <div
      className={`min-h-screen bg-gray-50 ${A.body} flex justify-center px-3 py-10`}
    >
      <div className={`${A.card} w-full max-w-md p-6`}>
        <div className="mb-4 flex justify-end"><UiLanguageSelect /></div>
        <div className="flex flex-col items-center text-center gap-1.5">
          {logoOk ? (
            <img
              src="/logo.svg?v=5"
              alt="nimos"
              className="block h-8 w-auto mx-auto"
              onError={() => setLogoOk(false)}
            />
          ) : (
            <span className="text-[18px] font-semibold tracking-tight text-gray-700">
              nimos
            </span>
          )}

          <h1 className={`mt-1 ${A.title}`}>{t("login.logIn")}</h1>
          <p className={A.label}>{t("login.welcomeBack")}</p>
        </div>

        {registered && !hasError && !needsVerification && (
          <div className={`${alertBase} border-green-200 bg-green-50 text-green-700`}>
            {t("login.registrationSuccess")}
          </div>
        )}

        {sessionExpired && !hasError && (
          <div className={`${alertBase} border-amber-200 bg-amber-50 text-amber-800`}>
            {t("login.sessionExpired")}
          </div>
        )}

        {passwordChanged && !hasError && (
          <div className={`${alertBase} border-green-200 bg-green-50 text-green-700`}>
            {t("login.passwordUpdated")}
          </div>
        )}

        {hasError && error && (
          <div className={`${alertBase} border-red-200 bg-red-50 text-red-700`}>
            {error.kind === "raw" ? error.value : error.kind === "known" ? t(error.key) : error.code === "noCompany" ? t("login.noCompany") : t("login.loginFailed")}
          </div>
        )}

        {needsVerification && (
          <div className={`${alertBase} border-sky-200 bg-sky-50 text-sky-800`}>
            <p className="mb-3">
              {t("login.verificationRequired")}
            </p>

            <Button
              type="button"
              variant="secondary"
              minWidth="auto"
              className="px-4 py-2 border-sky-200 text-sky-700 hover:bg-sky-50"
              onClick={handleResendVerification}
              disabled={resendDisabled}
            >
              {resendLabel}
            </Button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div>
            <label className={labelCls}>{t("directory.common.email")}</label>
            <input
              type="email"
              name="email"
              autoComplete="email"
              className={A.input}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                if (needsVerification) setResendStatus("idle");
              }}
              autoFocus
              required
            />
          </div>

          <div>
            <label className={labelCls}>{t("login.password")}</label>
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              className={A.input}
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          <Button type="submit" variant="primary" className="w-full justify-center">
            {t("login.logIn")}
          </Button>

          <div className="text-right">
            <Link
              to="/forgot-password"
              className={`${A.label} text-appleblue hover:underline`}
            >
              {t("login.forgotPassword")}
            </Link>
          </div>
        </form>

        <div className={`text-center mt-4 ${A.label}`}>
          {t("login.noAccount")}{" "}
          <Link to={`/register${upgradeIntentSearch(location.search)}`} className="text-appleblue hover:underline">
            {t("login.register")}
          </Link>
        </div>
      </div>
    </div>
  );
}
